import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudRainWind,
  CloudSnow,
  CloudSun,
  CloudSunRain,
  Cloudy,
  Droplets,
  Haze,
  type LucideIcon,
  Snowflake,
  Sun,
  ThermometerSnowflake,
  ThermometerSun,
  Tornado,
  Waves,
  Wind,
  WindArrowDown,
} from "lucide-react";

/** Pictogrammes de la météo (demande d'Alexis du 30/10/2026 : « agrémenter avec des symboles météo »), dans le
 *  trait des icônes de l'application : un par ciel du sélecteur (`environment.SKY_IDS` du serveur) et par clé
 *  d'aperçu propre au jeu saisonnier (`environment.PICTOGRAM_EXTRAS` : averses, chaleur, froid, vent, dégel). Deux
 *  ciels voisins peuvent partager un pictogramme (brouillard et brouillard givrant) : leur libellé les distingue ; les
 *  ciels de climats qui ne se croisent jamais aussi (blizzard et vent de sable). */
const PICTOGRAMS: Record<string, LucideIcon> = {
  clair: Sun,
  voile: CloudSun,
  couvert: Cloudy,
  brume: Haze,
  brouillard: CloudFog,
  brouillard_givrant: CloudFog,
  crachin: CloudDrizzle,
  pluie: CloudRain,
  pluie_forte: CloudRainWind,
  orage: CloudLightning,
  cevenol: CloudRainWind,
  neige_legere: CloudSnow,
  neige_forte: Snowflake,
  verglas: ThermometerSnowflake,
  mousson: Droplets,
  chaleur_humide: ThermometerSun,
  vent_de_sable: Wind,
  tempete_de_sable: Tornado,
  orage_crue: Waves,
  blizzard: Wind,
  averses: CloudSunRain,
  chaleur: ThermometerSun,
  froid: ThermometerSnowflake,
  vent: Wind,
  degel: Droplets,
};

export function pictogramIcon(key: string | null | undefined): LucideIcon {
  return (key && PICTOGRAMS[key]) || Cloud;
}

/** Pictogramme d'un vent du sélecteur ; « selon le ciel » reprend celui du ciel choisi. */
export function windIcon(id: string, sky: string | null | undefined): LucideIcon {
  if (id === "sky") return pictogramIcon(sky);
  if (id === "tempete") return Tornado;
  if (id === "regional") return WindArrowDown;
  return Wind;
}

/** Phase de la Lune dessinée d'après son éclairement : disque estompé, partie éclairée pleine, à droite quand elle
 *  croît vue de l'hémisphère nord (à gauche dans l'hémisphère sud). Terminateur : demi-ellipse de demi-axe
 *  |1 − 2k| × r (k : fraction éclairée), bombée vers le côté éclairé pour un croissant, vers l'autre au-delà du
 *  premier quartier. */
export function MoonPhase({
  pct,
  waxing,
  south = false,
  size = 14,
  className,
}: {
  pct: number;
  waxing: boolean;
  south?: boolean;
  size?: number;
  className?: string;
}) {
  const r = 7;
  const c = 8;
  const k = Math.min(1, Math.max(0, pct / 100));
  const right = waxing !== south;
  const rx = Math.abs(1 - 2 * k) * r;
  const outer = right ? 1 : 0;
  const terminator = right ? (k < 0.5 ? 0 : 1) : k < 0.5 ? 1 : 0;
  const lit = `M ${c} ${c - r} A ${r} ${r} 0 0 ${outer} ${c} ${c + r} A ${rx} ${r} 0 0 ${terminator} ${c} ${c - r} Z`;
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden className={className}>
      <circle cx={c} cy={c} r={r} fill="currentColor" opacity={0.16} />
      {k >= 0.98 ? <circle cx={c} cy={c} r={r} fill="currentColor" /> : k > 0.02 && <path d={lit} fill="currentColor" />}
      <circle cx={c} cy={c} r={r - 0.5} fill="none" stroke="currentColor" strokeWidth={1} opacity={0.55} />
    </svg>
  );
}
